import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import produce from 'immer';
import differenceBy from 'lodash/differenceBy';
import get from 'lodash/get';

import * as studentsConstants from 'common.components/students/constants';
import { StudentsActions } from 'common.components/students/typings';
import { Student } from 'common.components/typings';

import * as constants from './constants';
import { ClassesActions, ClassesState } from './typings';

function findStudentIndexById(studentsList: Array<Student>, id: number) {
    return studentsList.findIndex((student) => student.id === id);
}

const getInitialStatus = () => ({
    loadingStatus: LOADING_STATUSES.UNSENT,
    teachersLoadingStatus: LOADING_STATUSES.UNSENT,
    teachers: [],
});

const initialState: ClassesState = {};

const classesReducer = (state: ClassesState = initialState, action: ClassesActions | StudentsActions) => {
    // eslint-disable-next-line complexity
    return produce(state, (draft: ClassesState) => {
        switch (action.type) {
            case constants.GET_CLASS_REQUEST: {
                const { classId } = action;

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                draft[classId].loadingStatus = LOADING_STATUSES.LOADING;
                break;
            }

            case constants.GET_CLASS_SUCCESS: {
                const { classId, classData } = action;

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                draft[classId].loadingStatus = LOADING_STATUSES.SUCCESS;
                draft[classId].classData = classData;
                break;
            }

            case constants.GET_CLASS_ERROR: {
                const { classId } = action;

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                draft[classId].loadingStatus = LOADING_STATUSES.ERROR;

                break;
            }

            case constants.SET_NAME_REQUEST: {
                const { classId } = action;

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                draft[classId].nameSettingStatus = LOADING_STATUSES.LOADING;

                break;
            }

            case constants.SET_NAME_SUCCESS: {
                const { classId, letter, number } = action;

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                const { classData } = draft[classId];

                if (classData) {
                    classData.letter = letter;
                    classData.number = number;
                    draft[classId].nameSettingStatus = LOADING_STATUSES.SUCCESS;
                }

                break;
            }

            case constants.SET_NAME_ERROR: {
                const { classId } = action;

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                draft[classId].nameSettingStatus = LOADING_STATUSES.ERROR;

                break;
            }

            case studentsConstants.EDIT_STUDENT_NAME_SUCCESS: {
                // игнорим экшны, описанные в js-файлах, так как к ним нет тайпингов
                // написать "в лоб" через union почему-то не работает
                // @ts-ignore
                const { classId, id, firstName, lastName } = action;
                const stateClassData = draft[classId];

                if (!stateClassData) {
                    draft[classId] = getInitialStatus();
                }

                const editedStudentsList = get(stateClassData, 'classData.students');
                const studentIndex = findStudentIndexById(editedStudentsList, id);

                editedStudentsList[studentIndex].firstName = firstName;
                editedStudentsList[studentIndex].lastName = lastName;

                const { classData } = draft[classId];

                if (classData) {
                    classData.students = editedStudentsList;
                }

                break;
            }

            case studentsConstants.SAVE_NEW_STUDENT_SUCCESS: {
                // @ts-ignore
                const { classId, students } = action;
                const stateClassData = state[classId];

                if (!stateClassData) {
                    draft[classId] = getInitialStatus();
                }

                const oldStudents = get(stateClassData, 'classData.students');
                const newStudentData = differenceBy(students, oldStudents, (student: Student) => student.id)[0];

                const { classData } = draft[classId];

                if (classData) {
                    classData.students = [...oldStudents, newStudentData];
                }

                break;
            }

            case studentsConstants.RESET_STUDENT_LOGIN_SUCCESS: {
                const { classId, studentId, newLogin } = action;
                const stateClassData = state[classId];

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                const editedStudentsList = stateClassData?.classData?.students!;
                const studentIndex = findStudentIndexById(editedStudentsList, studentId);

                const { classData } = draft[classId];

                if (classData?.students) {
                    classData.students[studentIndex].login = newLogin;
                }

                break;
            }

            case studentsConstants.DELETE_STUDENT_REQUEST: {
                // @ts-ignore
                const { classId, userId } = action;
                const stateClassData = state[classId];

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                const editedStudentsList = get(stateClassData, 'classData.students');
                const studentIndex = findStudentIndexById(editedStudentsList, userId);

                const { classData } = draft[classId];

                if (classData && classData.students) {
                    classData.students![studentIndex].isDeleting = true;
                }

                break;
            }

            case studentsConstants.DELETE_STUDENT_SUCCESS: {
                // @ts-ignore
                const { classId, userId } = action;
                const stateClassData = state[classId];

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                const editedStudentsList = get(stateClassData, 'classData.students');
                const studentIndex = findStudentIndexById(editedStudentsList, userId);

                const { classData } = draft[classId];

                if (classData && classData.students) {
                    classData.students.splice(studentIndex, 1);
                }

                break;
            }

            case constants.GET_TEACHERS_REQUEST: {
                const { classId } = action;

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                draft[classId].teachersLoadingStatus = LOADING_STATUSES.LOADING;

                break;
            }

            case constants.GET_TEACHERS_SUCCESS: {
                const { classId, teachersList } = action;

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                draft[classId].teachersLoadingStatus = LOADING_STATUSES.SUCCESS;
                draft[classId].teachers = teachersList;

                break;
            }

            case constants.GET_TEACHERS_ERROR: {
                const { classId } = action;

                if (!state[classId]) {
                    draft[classId] = getInitialStatus();
                }

                draft[classId].teachersLoadingStatus = LOADING_STATUSES.ERROR;

                break;
            }

            default:
                break;
        }
    });
};

export default classesReducer;
